import { ChevronLeft, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { DataTable } from '../../components/DataTable';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import {
  useAssignMember,
  useDisableOrganization,
  useEnableOrganization,
  useOrganization,
  useOrgConfig,
  useOrgMembers,
  useRemoveMember,
  useRunOrganization,
  useUpdateOrgConfig,
} from '../../hooks/useOrganizations';
import { CRON_OPTIONS, isKnownCronSchedule } from '../../lib/cronOptions';
import { formatDate, formatDateTime } from '../../lib/format';
import type { OrgConfigPatch, OrgMember } from '../../types/domain';

function OrgConfigSection({ orgId }: { orgId: string }) {
  const { data, isLoading } = useOrgConfig(orgId);
  const mutation = useUpdateOrgConfig(orgId);
  const [dirty, setDirty] = useState<OrgConfigPatch>({});

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-16">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const current = { ...data, ...dirty };

  const hasUnknownCron =
    'cron_schedule' in dirty
      ? !isKnownCronSchedule(dirty.cron_schedule ?? '')
      : data
        ? !isKnownCronSchedule(data.cron_schedule)
        : false;

  const isDirty = Object.keys(dirty).length > 0;
  const canSave = isDirty && !hasUnknownCron && !mutation.isPending;

  function handleSave() {
    if (!canSave) return;
    mutation.mutate(dirty, {
      onSuccess: () => setDirty({}),
    });
  }

  function handleCancel() {
    setDirty({});
    mutation.reset();
  }

  const intFields: { key: keyof OrgConfigPatch; label: string }[] = [
    { key: 'min_delay_ms', label: 'Delay mínimo (ms)' },
    { key: 'max_delay_ms', label: 'Delay máximo (ms)' },
    { key: 'max_requests_per_minute', label: 'Máx. solicitudes/minuto' },
    { key: 'max_retries', label: 'Reintentos máximos' },
    { key: 'retry_delay_ms', label: 'Delay entre reintentos (ms)' },
  ];

  const cronValue =
    current.cron_schedule && isKnownCronSchedule(current.cron_schedule)
      ? current.cron_schedule
      : undefined;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {intFields.map(({ key, label }) => (
          <div key={key} className="space-y-1">
            <Label htmlFor={`config-${key}`} className="text-xs text-muted-foreground">
              {label}
            </Label>
            <Input
              id={`config-${key}`}
              type="number"
              min={0}
              value={(current[key] as number | undefined) ?? ''}
              onChange={(e) => setDirty((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
            />
          </div>
        ))}
      </div>

      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">Hora de ejecución (lunes a viernes)</Label>
        <Select
          value={cronValue}
          onValueChange={(v) => setDirty((prev) => ({ ...prev, cron_schedule: v }))}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar hora" />
          </SelectTrigger>
          <SelectContent>
            {CRON_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Switch
            id="config-email-notifications"
            checked={current.email_notifications_enabled ?? false}
            onCheckedChange={(checked) =>
              setDirty((prev) => ({ ...prev, email_notifications_enabled: checked }))
            }
          />
          <Label htmlFor="config-email-notifications" className="text-sm cursor-pointer">
            Notificaciones por email
          </Label>
        </div>
        <div className="flex items-center gap-3">
          <Switch
            id="config-notify-new-process"
            checked={current.notify_on_new_process ?? false}
            onCheckedChange={(checked) =>
              setDirty((prev) => ({ ...prev, notify_on_new_process: checked }))
            }
          />
          <Label htmlFor="config-notify-new-process" className="text-sm cursor-pointer">
            Notificar nuevo proceso
          </Label>
        </div>
      </div>

      {mutation.isSuccess && <p className="text-sm text-green-600">Configuración guardada.</p>}
      {mutation.isError && (
        <p className="text-sm text-destructive">{(mutation.error as Error).message}</p>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" size="sm" onClick={handleCancel} disabled={mutation.isPending}>
          Cancelar
        </Button>
        <Button size="sm" onClick={handleSave} disabled={!canSave}>
          {mutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Guardando...
            </>
          ) : (
            'Guardar'
          )}
        </Button>
      </div>
    </div>
  );
}

export function OrgDetail() {
  const { id } = useParams<{ id: string }>();
  const orgId = id ?? '';

  const { data: org, isLoading } = useOrganization(orgId);
  const { data: members = [] } = useOrgMembers(orgId);
  const disableMutation = useDisableOrganization();
  const enableMutation = useEnableOrganization();
  const runMutation = useRunOrganization();
  const assignMutation = useAssignMember(orgId);
  const removeMutation = useRemoveMember(orgId);

  const [disableOpen, setDisableOpen] = useState(false);
  const [enableOpen, setEnableOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<OrgMember | null>(null);
  const [newUserId, setNewUserId] = useState('');
  const [newRole, setNewRole] = useState<'owner' | 'member'>('member');

  function handleAssign() {
    if (!newUserId.trim()) return;
    assignMutation.mutate(
      { user_id: newUserId.trim(), role: newRole },
      {
        onSuccess: () => {
          setNewUserId('');
          setNewRole('member');
        },
      }
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="h-6 w-6 animate-spin rounded-full border-4 border-border border-t-primary" />
      </div>
    );
  }

  if (!org) {
    return <p className="text-muted-foreground">Organización no encontrada.</p>;
  }

  const memberColumns = [
    {
      header: 'User ID',
      accessor: (m: OrgMember) => <span className="font-mono text-xs">{m.user_id}</span>,
    },
    {
      header: 'Rol',
      accessor: (m: OrgMember) => (
        <Badge variant="secondary">{m.role === 'owner' ? 'Propietario' : 'Miembro'}</Badge>
      ),
    },
    { header: 'Unión', accessor: (m: OrgMember) => formatDate(m.created_at) },
    {
      header: '',
      accessor: (m: OrgMember) => (
        <Button size="sm" variant="destructive" onClick={() => setRemoveTarget(m)}>
          Eliminar
        </Button>
      ),
    },
  ];

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <Link
          to="/organizations"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft size={14} />
          Organizaciones
        </Link>

        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold">{org.name}</h1>
            <div className="flex items-center gap-2">
              {org.disabled ? (
                <Badge variant="destructive">Deshabilitada</Badge>
              ) : (
                <Badge variant="success">Activa</Badge>
              )}
              <Badge variant="secondary">
                {org.type === 'individual' ? 'Individual' : 'Equipo'}
              </Badge>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={() => runMutation.mutate(org.id)}
                disabled={org.disabled || runMutation.isPending}
              >
                Ejecutar ahora
              </Button>
              {org.disabled ? (
                <Button onClick={() => setEnableOpen(true)} disabled={enableMutation.isPending}>
                  Habilitar
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => setDisableOpen(true)}
                  disabled={disableMutation.isPending}
                >
                  Deshabilitar
                </Button>
              )}
            </div>
            {runMutation.isSuccess && (
              <p className="text-xs text-muted-foreground text-right max-w-xs">
                {runMutation.data.queued
                  ? 'Ya hay una corrida en curso. Esta organización quedó encolada.'
                  : 'Monitoreo iniciado. Puede tardar varios minutos.'}
              </p>
            )}
            {runMutation.isError && (
              <p className="text-xs text-destructive text-right max-w-xs">
                {(runMutation.error as Error).message}
              </p>
            )}
          </div>
        </div>
      </div>

      <section>
        <h2 className="text-base font-semibold mb-3">Metadatos</h2>
        <div className="flex flex-col divide-y divide-border rounded-lg border">
          {[
            { label: 'ID', value: <span className="font-mono text-xs">{org.id}</span> },
            { label: 'Identificador', value: org.identifier ?? '—' },
            { label: 'Email', value: org.email ?? '—' },
            { label: 'Creada', value: formatDateTime(org.created_at) },
            {
              label: 'Deshabilitada',
              value: org.disabled_at ? formatDateTime(org.disabled_at) : '—',
            },
          ].map(({ label, value }) => (
            <div key={label} className="flex items-baseline gap-6 px-4 py-3">
              <span className="text-xs text-muted-foreground w-32 flex-shrink-0">{label}</span>
              <span className="text-sm">{value}</span>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-base font-semibold mb-3">Miembros</h2>
        <DataTable
          columns={memberColumns}
          data={members}
          emptyMessage="Sin miembros."
          getRowKey={(m) => m.id}
        />

        <div className="mt-4 flex gap-3 items-end">
          <div className="flex-1 space-y-1">
            <Label htmlFor="user-id">User ID</Label>
            <Input
              id="user-id"
              value={newUserId}
              onChange={(e) => setNewUserId(e.target.value)}
              placeholder="uuid del usuario"
            />
          </div>
          <div className="w-36 space-y-1">
            <Label>Rol</Label>
            <Select value={newRole} onValueChange={(v) => setNewRole(v as 'owner' | 'member')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Miembro</SelectItem>
                <SelectItem value="owner">Propietario</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleAssign} disabled={assignMutation.isPending || !newUserId.trim()}>
            {assignMutation.isPending ? 'Agregando...' : 'Agregar'}
          </Button>
        </div>
        {assignMutation.isError && (
          <p className="mt-2 text-sm text-destructive">{(assignMutation.error as Error).message}</p>
        )}
      </section>

      <section>
        <h2 className="text-base font-semibold mb-3">Configuración</h2>
        <OrgConfigSection orgId={orgId} />
      </section>

      <ConfirmDialog
        open={disableOpen}
        onOpenChange={setDisableOpen}
        title="Deshabilitar organización"
        description={`¿Deshabilitar "${org.name}"?`}
        onConfirm={() => disableMutation.mutate(org.id, { onSuccess: () => setDisableOpen(false) })}
        confirmLabel="Deshabilitar"
        isDestructive
        isPending={disableMutation.isPending}
      />

      <ConfirmDialog
        open={enableOpen}
        onOpenChange={setEnableOpen}
        title="Habilitar organización"
        description={`¿Habilitar "${org.name}"?`}
        onConfirm={() => enableMutation.mutate(org.id, { onSuccess: () => setEnableOpen(false) })}
        confirmLabel="Habilitar"
        isPending={enableMutation.isPending}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setRemoveTarget(null);
        }}
        title="Eliminar miembro"
        description={`¿Eliminar al usuario ${removeTarget?.user_id} de esta organización?`}
        onConfirm={() => {
          if (!removeTarget) return;
          removeMutation.mutate(removeTarget.user_id, { onSuccess: () => setRemoveTarget(null) });
        }}
        confirmLabel="Eliminar"
        isDestructive
        isPending={removeMutation.isPending}
      />
    </div>
  );
}
